import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { countryData, codeToEmoji } from '../flags';

const NationalitySelector = ({ selectedCodes = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  
  const countryOptions = Object.entries(countryData).map(([name, code]) => ({
    name,
    code,
    emoji: codeToEmoji(code)
  }));

  const filteredOptions = countryOptions.filter(country =>
    !selectedCodes.includes(country.code) && (
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleSelect = (code) => {
    const newSelection = selectedCodes.includes(code)
      ? selectedCodes.filter(c => c !== code)
      : [...selectedCodes, code];
    onChange(newSelection);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleRemove = (codeToRemove, event) => {
    event.stopPropagation();
    onChange(selectedCodes.filter(code => code !== codeToRemove));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Nationalities
      </label>

      <div 
        className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedCodes.map(code => (
          <span
            key={code}
            className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-1 py-0 rounded-md text-sm"
          >
            {codeToEmoji(code)} {countryData[code]}
            <button
              type="button"
              onClick={(e) => handleRemove(code, e)}
              className="hover:text-blue-600"
            >
              <X size={14} />
            </button>
          </span>
        ))}
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedCodes.length === 0 ? "Search countries..." : ""}
          className="flex-1 outline-none min-w-[120px] text-sm"
        />
      </div>

      {/* Dropdown menu */}
      {isOpen && searchTerm && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">No results found</div>
            ) : (
              filteredOptions.map(({ code, name, emoji }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => handleSelect(code)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                >
                  <span>{emoji}</span>
                  <span>{name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NationalitySelector;